'use client'

import { useTranslation } from '@shared/config/i18n'
import { useMemo, useState } from 'react'

import {
  createRegisterCardFormSchema,
  RegisterCardFormErrors,
  RegisterCardFormState,
  RegisterCardFormValues,
} from '../schemas'

export const useRegisterCardForm = () => {
  const t = useTranslation()
  const registerCardFormSchema = useMemo(() => createRegisterCardFormSchema(t), [t])
  const formKeys = useMemo(() => Object.keys(registerCardFormSchema._def.shape()), [registerCardFormSchema])
  const [formState, setFormState] = useState<RegisterCardFormState>({
    values: formKeys.reduce((acc, key) => ({ ...acc, [key]: '' }), {} as RegisterCardFormValues),
    errors: formKeys.reduce(
      (acc, key) => ({ ...acc, [key]: { hasError: true, errorMessage: '' } }),
      {} as RegisterCardFormErrors,
    ),
  })

  const validationNumeric = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = e.target.value.replace(/[^\d]/g, '')
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    const result = validateFormItemByName(name as keyof RegisterCardFormValues, value)

    if (result?.success) {
      setFormState((prev) => ({
        values: { ...prev.values, [name]: value },
        errors: { ...prev.errors, [name]: { hasError: false } },
      }))
    } else {
      setFormState((prev) => ({
        values: { ...prev.values, [name]: value },
        errors: { ...prev.errors, [name]: { hasError: true, errorMessage: result?.errorMessage || '' } },
      }))
    }
  }

  const validateFormItemByName = (name: keyof RegisterCardFormValues, value: string) => {
    const validationResult = registerCardFormSchema._def.shape()[name].safeParse(value)

    if (validationResult.success) {
      return { success: true }
    }

    if (validationResult.error) {
      return { success: false, errorMessage: validationResult.error.errors[0].message }
    }
  }

  const isFormValid = formKeys.every((key) => !formState.errors[key as keyof RegisterCardFormErrors].hasError)

  return { formState, validationNumeric, handleChange, isFormValid }
}
